import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import TierBadge from "@/components/tier-badge";
import { Trash } from "@phosphor-icons/react";
import { formatDateTime } from "@/lib/date";

export default function AdminAmbassadors() {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStaffId, setBulkStaffId] = useState("");

  const loadAll = async () => {
    try {
      const [u, s] = await Promise.all([api.get("/admin/ambassadors"), api.get("/admin/staff")]);
      setUsers(u.data);
      setStaff(s.data);
    } catch (_) { /* ignore load errors */ }
  };
  useEffect(() => { loadAll(); }, []);

  const suspend = async (id) => { await api.post(`/admin/ambassadors/${id}/suspend`); toast.success("Suspended"); loadAll(); };
  const activate = async (id) => { await api.post(`/admin/ambassadors/${id}/activate`); toast.success("Activated"); loadAll(); };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name} ${u.surname}?\n\nThis will PERMANENTLY delete the ambassador AND all their referrals + rewards. This action cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/admin/ambassadors/${u.id}`);
      toast.success(`Deleted (${data.deleted.referrals} referrals, ${data.deleted.rewards} rewards removed)`);
      loadAll();
    } catch (e) { toast.error("Delete failed"); }
  };

  const toggle = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const toggleAll = () => {
    if (selectedIds.size === users.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(users.map((u) => u.id)));
  };

  const bulkAssign = async () => {
    if (selectedIds.size === 0) return toast.error("Select at least one ambassador");
    const staffId = bulkStaffId || null;
    try {
      await Promise.all([...selectedIds].map((id) =>
        api.post(`/admin/ambassadors/${id}/assign-staff`, { staff_id: staffId })
      ));
      toast.success(`Assigned ${selectedIds.size} ambassador(s)`);
      setSelectedIds(new Set());
      loadAll();
    } catch (e) { toast.error("Failed to assign"); }
  };

  const staffName = (id) => id ? (staff.find((s) => s.id === id)?.name || "—") : t.admin.unassigned;

  return (
    <div className="space-y-6">
      <h1 className="font-serif-display text-4xl">{t.admin.ambassadors}</h1>

      {selectedIds.size > 0 && (
        <div className="rounded-2xl bg-[#f4a261]/10 border border-[#f4a261]/30 p-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-white">{selectedIds.size} selected</span>
          <select data-testid="bulk-staff-select" value={bulkStaffId} onChange={(e) => setBulkStaffId(e.target.value)} className="bg-[#0b1521] border border-white/20 rounded-full px-3 py-1.5 text-xs">
            <option value="">{t.admin.unassigned}</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button data-testid="bulk-assign-btn" onClick={bulkAssign} className="px-4 py-1.5 rounded-full bg-[#f4a261] text-[#0b1521] text-xs font-medium">
            Assign to staff
          </button>
          <button onClick={() => setSelectedIds(new Set())} className="text-xs text-white/60 hover:text-white">Clear</button>
        </div>
      )}

      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        {users.length === 0 ? (
          <div className="p-10 text-center text-sm text-white/60">No ambassadors yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-white/50 border-b border-white/10">
              <tr>
                <th className="p-4 w-8"><input data-testid="select-all-checkbox" type="checkbox" checked={selectedIds.size === users.length && users.length > 0} onChange={toggleAll} /></th>
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Ref code</th>
                <th className="text-left p-4">Level</th>
                <th className="text-left p-4">Qual/Total</th>
                <th className="text-left p-4">Registered</th>
                <th className="text-left p-4">{t.admin.assignedTo}</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`amb-row-${u.id}`} className="border-b border-white/5 last:border-0">
                  <td className="p-4"><input data-testid={`select-amb-${u.id}`} type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggle(u.id)} /></td>
                  <td className="p-4">{u.name} {u.surname}<br/><span className="text-white/50 text-xs">{u.email}</span></td>
                  <td className="p-4 font-mono text-xs">{u.ref_code}</td>
                  <td className="p-4"><TierBadge level={u.level || "Starter"} size="sm" /></td>
                  <td className="p-4">{u.qualified_count}/{u.total_referrals}</td>
                  <td className="p-4 text-xs text-white/70">{formatDateTime(u.created_at)}</td>
                  <td className="p-4 text-xs text-white/70">{staffName(u.assigned_staff_id)}</td>
                  <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs ${u.status === "active" ? "bg-[#f4a261]/20 text-[#f4a261]" : "bg-red-500/20 text-red-300"}`}>{u.status}</span></td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {u.status === "active" ? (
                        <button data-testid={`suspend-${u.id}`} onClick={() => suspend(u.id)} className="px-3 py-1.5 rounded-full border border-white/20 text-xs">{t.admin.suspend}</button>
                      ) : (
                        <button data-testid={`activate-${u.id}`} onClick={() => activate(u.id)} className="px-3 py-1.5 rounded-full bg-[#f4a261] text-[#0b1521] text-xs">{t.admin.activate}</button>
                      )}
                      <button data-testid={`delete-amb-${u.id}`} onClick={() => remove(u)} title="Delete permanently" className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10">
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
